import { sfQuery, dataSources } from '../../../API';
import { calculatePostDate } from '../utils/dateUtils';

/**
 * Upload content plan to Salesforce as Social_Media_Post__c records
 * 
 * @param {string} accountId - Salesforce Account ID
 * @param {Array} contentPlan - Array of week objects with posts
 * @param {string} startDate - Start date in YYYY-MM-DD format
 * @param {string} postSchedule - Post schedule (MW or TTH)
 * @param {Object} context - Auth context
 * @returns {Promise<Object>} - Upload result with success status and details
 */
export const uploadToSalesforce = async (accountId, contentPlan, startDate, postSchedule, context) => {
    try {
        // Step 1: Query for the Account's Social_Media__c record
        const socialMediaRecords = await sfQuery(
            "Social_Media__c",
            "Id, Name, Account__c, Platforms_Selected__c",
            `Account__c = '${accountId}'`,
            context
        );

        if (!socialMediaRecords || socialMediaRecords.length === 0) {
            throw new Error('No Social Media record found for this account. Please ensure the account has a Social Media record in Salesforce.');
        }

        const socialMediaRecord = socialMediaRecords[0];
        const socialMediaId = socialMediaRecord.Id;
        const platformsSelected = socialMediaRecord.Platforms_Selected__c || '';

        // Parse selected platforms (Multi-Select Picklist is usually semicolon separated)
        const hasFacebook = platformsSelected.includes('Facebook');
        const hasInstagram = platformsSelected.includes('Instagram');
        const hasLinkedIn = platformsSelected.includes('LinkedIn');
        const hasTwitter = platformsSelected.includes('Twitter');
        const hasGMB = platformsSelected.includes('Google My Business');

        // Step 2: Transform content plan into Social_Media_Post__c records
        const postsToCreate = [];

        contentPlan.forEach((week, weekIndex) => {
            week.posts.forEach((post, postIndex) => {
                const postDate = calculatePostDate(startDate, weekIndex, postIndex, postSchedule);

                // Format the post content (title + caption)
                const postContent = `${post.title}\n\n${post.caption}`;

                postsToCreate.push({
                    Social_Media__c: socialMediaId,
                    Date_of_Post__c: postDate.toISOString().split('T')[0], // YYYY-MM-DD format
                    Post_Subject_Title__c: postContent, // Use the combined title/caption for the main content field
                    Cycle__c: '1', // Assuming cycle 1 for new uploads (Picklist)
                    Post_Drafted__c: false, // Leave as not drafted (open)
                    Hold_Up__c: 'Proposed', // Set status to Proposed

                    // Map Platform Checkboxes
                    Facebook__c: hasFacebook,
                    Instagram__c: hasInstagram,
                    LinkedIn__c: hasLinkedIn,
                    Twitter__c: hasTwitter,
                    Google_My_Business__c: hasGMB
                });
            });
        });

        // Step 3: Batch create posts using Salesforce REST API
        // We'll use the composite API to create multiple records at once
        const batchSize = 200; // Salesforce allows up to 200 records per batch
        const batches = [];

        for (let i = 0; i < postsToCreate.length; i += batchSize) {
            batches.push(postsToCreate.slice(i, i + batchSize));
        }

        const results = [];
        for (const batch of batches) {
            const batchResult = await createPostsBatch(batch);
            results.push(...batchResult);
        }

        // Step 4: Check for any failures
        const failures = results.filter(r => !r.success);

        if (failures.length > 0) {
            console.error('Some posts failed to create:', failures);
            return {
                success: false,
                message: `${failures.length} out of ${results.length} posts failed to upload. Please check the console for details.`,
                details: {
                    total: results.length,
                    successful: results.length - failures.length,
                    failed: failures.length,
                    failures: failures
                }
            };
        }

        return {
            success: true,
            message: `Successfully uploaded ${results.length} posts to Salesforce!`,
            details: {
                total: results.length,
                successful: results.length,
                failed: 0,
                socialMediaId: socialMediaId
            }
        };

    } catch (error) {
        console.error('Error uploading to Salesforce:', error);
        return {
            success: false,
            message: error.message || 'Failed to upload posts to Salesforce. Please try again.',
            error: error
        };
    }
};

/**
 * Create a batch of posts using Salesforce SDK
 *
 * @param {Array} posts - Array of post objects to create
 * @returns {Promise<Array>} - Array of results
 */
const createPostsBatch = async (posts) => {
    try {
        // Get organizationId from environment variable (same as other API functions)
        const organizationId = process.env.REACT_APP_DEFAULT_ORGANIZATION_ID;

        if (!organizationId) {
            throw new Error('Organization ID is required for Salesforce operations. Please set REACT_APP_DEFAULT_ORGANIZATION_ID environment variable.');
        }

        console.log('Creating batch of posts:', {
            organizationId: organizationId.substring(0, 8) + '...',
            postsCount: posts.length,
            firstPost: posts[0]
        });

        // Use dataSources.salesforce.insert to create records
        const result = await dataSources.salesforce.insert(organizationId, {
            objectName: 'Social_Media_Post__c',
            records: posts
        });

        console.log('Batch insert result:', result);
        console.log('Result data:', result.data);

        // The SDK returns an Axios response object, extract the actual data
        const responseData = result.data || result;

        console.log('Response data type:', Array.isArray(responseData) ? 'array' : typeof responseData);
        console.log('Response data:', responseData);

        // The response has the format: {result: Array, source: 'salesforce'}
        // Extract the result array
        const resultArray = responseData.result || responseData;

        console.log('Final result array:', resultArray);

        // Ensure we return an array
        return Array.isArray(resultArray) ? resultArray : [resultArray];
    } catch (error) {
        console.error('Error creating posts batch:', error);
        console.error('Error details:', {
            message: error.message,
            response: error.response?.data,
            stack: error.stack
        });
        throw error;
    }
};

/**
 * Validate that an account has the necessary Social Media configuration
 * 
 * @param {string} accountId - Salesforce Account ID
 * @param {Object} context - Auth context
 * @returns {Promise<boolean>} True if account is valid for upload
 */
export const validateAccountForUpload = async (accountId, context) => {
    try {
        const socialMediaRecords = await sfQuery(
            "Social_Media__c",
            "Id",
            `Account__c = '${accountId}'`,
            context
        );

        return socialMediaRecords && socialMediaRecords.length > 0;
    } catch (error) {
        console.error('Error validating account:', error);
        return false;
    }
};

/**
 * Fetch past social media posts from Salesforce for a given account
 * Used to provide context to LLM and avoid duplicate content
 * 
 * @param {string} accountId - Salesforce Account ID
 * @param {Object} context - Auth context
 * @returns {Promise<string>} Formatted past posts text for LLM, or empty string if none found
 */
export const fetchPastPostsFromSalesforce = async (accountId, context) => {
    try {
        console.log('Fetching past posts from Salesforce for account:', accountId);

        // Step 1: Get the Social_Media__c record for this account
        const socialMediaRecords = await sfQuery(
            "Social_Media__c",
            "Id",
            `Account__c = '${accountId}'`,
            context
        );

        if (!socialMediaRecords || socialMediaRecords.length === 0) {
            console.log('No Social_Media__c record found for this account');
            return '';
        }

        const socialMediaId = socialMediaRecords[0].Id;
        const today = new Date().toISOString().split('T')[0];

        // Step 2: Get recent posts (last 100 posts, most recent first)
        // Filter for posts BEFORE today to get actual past examples, not future scheduled ones
        // Fetch multiple potential content fields
        let posts = [];
        try {
            posts = await sfQuery(
                "Social_Media_Post__c",
                "Id, Name, Post_Subject_Title__c, Content_Needed__c, Notes__c, Date_of_Post__c",
                `Social_Media__c = '${socialMediaId}' AND Date_of_Post__c < ${today} ORDER BY Date_of_Post__c DESC LIMIT 100`,
                context
            );
        } catch (error) {
            console.warn('Primary past posts query failed, trying fallback:', error);
            try {
                posts = await sfQuery(
                    "Social_Media_Post__c",
                    "Id, Date_of_Post__c",
                    `Social_Media__c = '${socialMediaId}' AND Date_of_Post__c < ${today} ORDER BY Date_of_Post__c DESC LIMIT 100`,
                    context
                );
            } catch (fatalError) {
                console.error('All past posts queries failed:', fatalError);
                return '';
            }
        }

        if (!posts || posts.length === 0) {
            console.log('No past posts found for this account (before today)');
            return '';
        }

        console.log(`Found ${posts.length} actual past posts from Salesforce`);

        // Step 3: Format posts for LLM consumption
        const formattedPosts = posts.map((post, index) => {
            const date = post.Date_of_Post__c || 'No date';
            // Check all potential content fields
            const content = post.Post_Subject_Title__c || post.Content_Needed__c || post.Notes__c || '(No content)';

            return `${index + 1}. [${date}]\n${content}`;
        }).join('\n\n');

        const result = `Past posts from Salesforce (${posts.length} most recent posts):\n\n${formattedPosts}`;

        console.log('Successfully formatted past posts for LLM');
        return result;

    } catch (error) {
        console.error('Error fetching past posts from Salesforce:', error);
        // Don't throw - gracefully degrade by returning empty string
        return '';
    }
};
